import { getPossibleColorSchemes } from "./schemes";
import { SingularityBrandColor } from "@/types/singularity/brand";
import { ColorScheme } from "./types";

/**
 * CSS template for a single color scheme
 * Uses descriptive CSS variable names that match the ColorScheme type
 */
const SCHEME_CSS_TEMPLATE = (className: string, scheme: ColorScheme): string => `${className} {
  --background: var(--${scheme.background.cssVariableName});
  --contrasted-from-background-for-text: var(--${scheme.contrastedFromBackgroundForText.cssVariableName});
  --contrasted-from-background-for-button: var(--${scheme.contrastedFromBackgroundForButton.cssVariableName});
  --contrasted-from-background-for-button-hover: var(--${scheme.contrastedFromBackgroundForButtonHover.cssVariableName});
  --contrasted-from-button-for-button-text: var(--${scheme.contrastedFromButtonForButtonText.cssVariableName});
  --contrasted-from-button-for-button-text-hover: var(--${scheme.contrastedFromButtonForButtonTextHover.cssVariableName});
}`;

/**
 * Generate CSS classes for all color schemes
 * Creates utility classes like .primary-scheme-1, .neutral-scheme-2, etc.
 *
 * @param brandColors The brand colors from singularityStore.brand.color
 * @returns CSS string ready to be injected into the global settings
 */
export function generateColorSchemesCSS(brandColors: SingularityBrandColor): string {
  const schemes = getPossibleColorSchemes(brandColors);
  const cssLines: string[] = [];

  cssLines.push('/* FutureLayer Color Schemes - WCAG Compliant */');
  cssLines.push('/* Auto-generated from brand colors */');
  cssLines.push('');

  // Generate Primary Color Schemes
  if (schemes.primary && schemes.primary.length > 0) {
    cssLines.push('/* Primary Color Schemes */');
    schemes.primary.forEach((scheme, index) => {
      cssLines.push(SCHEME_CSS_TEMPLATE(`.primary-scheme-${index + 1}`, scheme));
      cssLines.push('');
    });
  }

  // Generate Neutral Color Schemes
  if (schemes.neutral && schemes.neutral.length > 0) {
    cssLines.push('/* Neutral Color Schemes */');
    schemes.neutral.forEach((scheme, index) => {
      cssLines.push(SCHEME_CSS_TEMPLATE(`.neutral-scheme-${index + 1}`, scheme));
      cssLines.push('');
    });
  }

  // Generate Secondary Color Schemes
  if (schemes.secondary && schemes.secondary.length > 0) {
    cssLines.push('/* Secondary Color Schemes */');
    schemes.secondary.forEach((paletteSchemes, paletteIndex) => {
      if (paletteSchemes && paletteSchemes.length > 0) {
        cssLines.push(`/* Secondary Palette ${paletteIndex + 1} */`);
        paletteSchemes.forEach((scheme, schemeIndex) => {
          cssLines.push(SCHEME_CSS_TEMPLATE(`.secondary-${paletteIndex + 1}-scheme-${schemeIndex + 1}`, scheme));
          cssLines.push('');
        });
      }
    });
  }

  return cssLines.join('\n');
}
